/*
* 项目业务级功能方法汇总文件
* 通用性公共方法记得迭代到 @lhb/func 方法库
*/
import { floorKeep, matchQuery, isNotEmpty, parseArrayToString, dateFns } from '@lhb/func';
import dayjs from 'dayjs';
import { message } from 'antd';
import V2Message from '../components/Others/V2Hint/V2Message';
import { get } from '../request';

/*
* 用来把接口返回的选项数据，name，id转为 label value
* 我们的接口爱用 name id，antd默认 label，value。 甚至radio这种都不允许改key，贼烦，故有此方法函数
*/
function _mapTree(org) {
  const haveChildren = Array.isArray(org.children) && org.children.length;
  return {
    label: org.name,
    value: org.id,
    children: haveChildren ? org.children.map(item => _mapTree(item)) : []
  };
}
export function refactorSelection(selection: any[] = []) {
  return selection.map((item: any) => {
    return _mapTree(item);
  });
}

// 逆地理编码(经纬度转换成具体地址)
/**
 * @param {Array} lnglat 经纬度
 * @param {String} cityName 城市名
 * @param {Boolean} onlyAddress 是否仅返回具体地址
 */
export function getLngLatAddress(geocoder, lnglat, onlyAddress = true) {
  return new Promise((resolve, reject) => {
    geocoder.getAddress(lnglat, (status, result) => {
      if (status === 'complete' && result.regeocode) {
        const { regeocode } = result;
        if (onlyAddress) {
          const { formattedAddress } = regeocode || {};
          resolve(formattedAddress);
        } else {
          resolve(regeocode);
        }
      } else {
        reject(new Error(`根据经纬度查询地址失败`));
      }
    });
  });
}

/**
 * 计算2个日期相差的天数，包含今天，如：2016-12-13到2016-12-15，相差3天
 * @param startDateString
 * @param endDateString
 * @returns
 */
export function dateDiffIncludeToday(startDateString, endDateString) {
  const startDate:number = +new Date(startDateString);
  const endDate:number = +new Date(endDateString);
  return (Math.abs(endDate - startDate) / 1000 / 60 / 60 / 24) + 1;// 把相差的毫秒数转换为天数
};

/**
 * 判断数字是否在范围区间内
 * @param num 目标数字，如 2
 * @param rangeArr 范围区间，如 [1, 3]
 * @return true/false
 * @demo isInRange(2, [1, 3])
 */
export function isInRange(num, range) {
  if (!isNotEmpty(num) || !Array.isArray(range) || range.length !== 2) {
    return false;
  }
  const min = Math.min(...range);
  const max = Math.max(...range);
  return Math.max(min, num) === Math.min(num, max);
}

/**
 * 平铺多维数组
 * @param {Array<any>}
 * @returns
 */
export function tileArr (arr: any[]) {
  return arr.reduce((acc, cur) =>
    acc.concat(Array.isArray(cur) ? tileArr(cur) : cur), []);
}

/**
 * [showInvalidFieldMsg 解析el-form校验未通过的字段信息]
 * @param  {[type]} object    未通过校验的字段
 * @param  {[type]} maxLength 提示内容的最大个数，默认 -1无限制
 * @return {[type]}        [description]
 */
export function showInvalidFieldMsg(errorFields, maxLength = -1) {
  if (!Array.isArray(errorFields) || !errorFields.length) {
    return null;
  }
  const arr: any = [];
  errorFields.forEach(item => {
    const data = parseArrayToString(item.errors);
    if (data) {
      arr.push(data);
    }
  });

  const messages = maxLength === -1 ? arr : arr.slice(0, maxLength);
  message.warning(parseArrayToString(messages));
  // window.microMessage.warning(parseArrayToString(messages));

  return messages;
}

/**
 * 滚动到第一个必填未填项
 */
export function scrollToError(trueDocument = document) {
  setTimeout(() => {
    const errorLists = trueDocument.querySelector('.ant-form-item-has-error');
    console.log('errorLists', errorLists);
    errorLists?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // errorLists && errorLists[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 0);
};

/**
 *
 * @returns 该应用是否为内嵌页面
 */
export function isSource() {
  return !!matchQuery(location.search, 'source');
}

export const isNumber = (value: unknown): value is number => typeof value === 'number';

// 处理日期为 年月日
export const decodeDate = (date, format = 'YYYY-MM-DD') => {
  return isNotEmpty(date) ? dayjs(date).format(format) : null;
};

// 处理日期为 dayjs
export const encodeDate = (date, format?) => {
  return isNotEmpty(date) ? dayjs(date, format) : null;
};


/**
 * 下载 blob 数据
 * @description: 前端 axios 下载 excel，并解决 axios 返回 header 无法获取所有数据的问题 https://www.cnblogs.com/smiler/p/8708815.html
 * @param {*} fileName 文件名称
 * @return {*}
 */
export function downloadBlob(data, fileName) {
  const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' }); // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet这里表示xlsx类型
  // 允许用户在客户端上保存文件
  if (navigator['msSaveOrOpenBlob'] && navigator['msSaveBlob']) {
    navigator['msSaveBlob'](blob, fileName);
  } else {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob); // 创建下载的链接
    link.download = fileName; // 下载后文件名
    document.body.appendChild(link);
    link.click(); // 点击下载
    document.body.removeChild(link); // 下载完成移除元素
    window.URL.revokeObjectURL(link.href); // 释放掉blob对象
  }
}

/**
 * @description: 将日期数组处理成日期范围[2023-01-01,2023-01-02,2023-01-03,]=>[2023-01-01~2023-01-03]
 * @param {*} dateArr 日期数组
 * @return {*}
 */
export function parseDateArrToDateRange(dateArr) {
  const dates: string[] = [];
  let cacheRange: number[] = [];
  dateArr && dateArr.sort().forEach((timer, index) => {
    timer = dayjs(timer).valueOf();
    const lastTimer = dateArr[index + 1] && dayjs(dateArr[index + 1]).valueOf();
    if (lastTimer && +floorKeep(timer, dateFns.day, 2) === lastTimer) {
      cacheRange.push(timer);
      return;
    }
    // 如果cacheRange有数据
    if (cacheRange.length) {
      dates.push(`${dayjs(cacheRange[0]).format('YYYY.MM.DD')}~${dayjs(timer).format('YYYY.MM.DD')}`);
      cacheRange = [];
    } else {
      dates.push(dayjs(timer).format('YYYY.MM.DD'));
    }
  });
  return dates;
}

// 判断浏览区是否支持canvas
export function isSupportCanvas() {
  const elem = document.createElement('canvas');
  return !!(elem.getContext && elem.getContext('2d'));
}

/**
 * 计算图表类
 */
export function echartsFormatUnit(data) { // data | Array
  const minVal = Math.min.apply(Math, data); // 最小值
  const unit = 10000;
  if ((minVal / unit) >= 1) { // 以万为单位
    return {
      name: 'w',
      value: 10000
    };
  } else {
    return {
      name: '',
      value: 1
    };
  }
}

/**
 * 将数字转成k、w结尾
 * @param num 值
 * @returns 1000转化为1k，10000转为1w,10000000转为1kw
 */
export function formatNumber(num: number) {
  // return num >= 1e3 && num < 1e4 ? (num / 1e3).toFixed(1) + 'k' : num >= 1e4 ? (num / 1e4).toFixed(1) + 'w' : num;
  if (num >= 1e3 && num < 1e4) {
    return (num / 1e3).toFixed(1) + 'k';
  } else if (num >= 1e4 && num < 1e8) {
    return (num / 1e4).toFixed(1) + 'w';
  } else if (num >= 1e8) {
    return (num / 1e8).toFixed(1) + '亿';
  } else {
    return num;
  }
}

/**
 * 打开高德地图网页
 * @param longitude 经度
 * @param latitude 纬度
 * @param address 地名
 */
export function openAMap(longitude: string|number, latitude:string|number, address:string) {
  return window.open(`https://ditu.amap.com/regeo?lng=${longitude}&lat=${latitude}&name=${address}&src=uriapi&callnative=1&innersrc=uriapi`);
}

/**
 * @description: 递归遍历对象，清空参数前后空格
 * @param {type} obj 递归遍历对象
 * @param {type} isDelEmptyObject 是否删除空内容（空值、空对象、空数组），默认不删除
 * @demo deepTrim({ id: ' 1 ' }); // { id: '1' }
 */
export function deepTrim(obj, isDelEmptyObject = false) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  const keys = Object.keys(obj);
  for (var key of keys) {
    const val = obj[key];
    if (!isNotEmpty(val)) {
      isDelEmptyObject && Reflect.deleteProperty(obj, key);
    } else if (typeof val === 'string') {
      // 去空格
      obj[key] = val.trim();

      // 判断是否需要删除空值
      isDelEmptyObject && !isNotEmpty(obj[key]) && Reflect.deleteProperty(obj, key);
    } else if (Array.isArray(val)) {
      isDelEmptyObject && val.length === 0 && Reflect.deleteProperty(obj, key);
    } else if (val instanceof Object) {
      // 属性值为对象，递归调用
      deepTrim(obj[key], isDelEmptyObject);

      // 如某属性的值为不包含任何属性的独享，则将该属性删除，即 如果是空数组或空对象，删除该属性
      isDelEmptyObject && Object.keys(obj[key]).length === 0 && Reflect.deleteProperty(obj, key);
    }
  }
  return obj;
}

/**
 * @description base64 图片转 Blob
 * @param base64
 * @return
 */
export function base64ToBlob(base64) {
  const binary = atob(base64);
  const array: any[] = [];
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
  }
  return new Blob([new Uint8Array(array)], { type: 'image/png' });
}

/**
 * @description: 获取元素指定的 style 属性
 * 如果获取像素，一般结合 parseInt 获取数值，如 parseInt(getStyle(el, 'margin-top'), 10)
 * @param {*} element
 * @param {*} styleName
 * @return {*}
 */
export const getStyle = function(element, styleName) {
  if (!element || !styleName) return null;
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView?.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed?.[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/**
 * 时间显示格式化（时间戳转为 xx天xx小时）
 * @description
 * @param t 时间戳
 * @param cutCount 截取显示前 n 个时间点，大于 0 时生效，如 times = 2 时，'1天1小时1分1秒' => '1天1小时'
 * @return
 * @demo durationStr(5760000); // 1小时36分
 * durationStr(1000*1 + 1000*60 + 1000*60*60 + 1000*60*60*24 + 1000*60*60*24*365, 2); // 1年1天
 */
export const durationStr = function(t, cutCount = -1) {
  const second = 1000;
  const minute = second * 60;
  const hour = minute * 60;
  const day = hour * 24;
  const year = day * 365;

  const timeTypes = [
    { value: 'year', unit: '年', timestamp: year },
    { value: 'day', unit: '天', timestamp: day },
    { value: 'hour', unit: '小时', timestamp: hour },
    { value: 'minute', unit: '分钟', timestamp: minute },
    { value: 'second', unit: '秒', timestamp: second },
  ];

  let timeArr = timeTypes.reduce((result: any, item) => {
    if (t >= item.timestamp) {
      result.push(parseInt(String(t / item.timestamp)) + item.unit);
      t %= item.timestamp;
    }
    return result;
  }, []);
  if (cutCount > 0) {
    timeArr = timeArr.slice(0, cutCount);
  }
  return timeArr.join('');
};

/**
 * 唤起企业微信联系人（浏览器 schema 协议）
 * https://developer.work.weixin.qq.com/document/path/94345
 * @description
 * @param params.id 账号中心用户 ID
 * @param params.apiUrl 接口地址
 * @return
 * @demo
 */
export async function openEnterpriseChatSchemaByUserId({ userId, apiUrl = '/requirement/chat' }: { userId: number, apiUrl?: string }) {
  if (!userId) {
    return;
  }
  const url: any = await getEnterpriseChatSchemaUrlByUserId({ userId, apiUrl });
  console.log('url', url);
  url && (window.location.href = url);
}

/**
 * 获取唤起企业微信联系人（浏览器 schema 协议）的 schema 链接
 * https://developer.work.weixin.qq.com/document/path/94345
 * @description
 * @param params.userId 账号中心用户 ID
 * @param params.apiUrl 接口地址
 * @return
 * @demo
 */
export function getEnterpriseChatSchemaUrlByUserId({ userId, apiUrl = '/requirement/chat' }: { userId: number, apiUrl?: string }) {
  if (!userId) {
    return;
  }
  apiUrl = apiUrl || '/requirement/chat';
  // https://yapi.lanhanba.com/project/307/interface/api/53290
  return get(apiUrl, { userId }, true).then((res) => {
    if (res) {
      return res;
    } else {
      V2Message.warning('暂未绑定企业微信账号');
    }
  });
}

/**
 * 向父级页面发送消息
 * @description
 * @param message
 * @param targetOrigin
 * @return
 * @demo
  postMessageToParent({
    type: 'createdSucceed',
    data: '保存需求成功'
  }, '*'); // 告诉父页面创建需求成功
 */
export function postMessageToParent(message: { type: string, data: any }, targetOrigin = '*') {
  window.parent.postMessage(message, targetOrigin); // 告诉父页面创建需求成功
}

/**
 * 截取掉文本内的省市区
 * @description
 * @param str
 * @return
 * @demo cutProvinceCityDistrictFromText('浙江省杭州市西湖区浙江省博物馆'); // 浙江省博物馆
 */
export const cutProvinceCityDistrictFromText = (str: String) => {
  if (!isNotEmpty(str)) {
    return '';
  }
  const curStr = String(str);
  // 优先级
  return curStr.slice((curStr.indexOf('区') + 1) || (curStr.indexOf('市') + 1) || (curStr.indexOf('省') + 1));
};
